import { PageHeader, LoadingState, DataTable, FilterBar, ActionToolbar, EmptyState } from '../components/AdminPageUI';
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Grid, Button, TextField, MenuItem,
  Table, TableHead, TableRow, TableCell, TableBody,
  CircularProgress, IconButton, Card, CardContent,
  Container, Avatar, Stack, Tooltip, Chip, Dialog,
  DialogTitle, DialogContent, DialogActions, Alert,
  Collapse, FormControlLabel, Checkbox, Divider,
  Paper, useMediaQuery, alpha, useTheme
} from '@mui/material';
import {
  Add, Person, FamilyRestroom, Email, Phone,
  ChildCare, Group, ExpandMore, ExpandLess,
  Edit, Delete, Close
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../utils/Api';

export default function ParentsPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [parents, setParents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [open, setOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    childrenIds: []
  });
  const [parentToDelete, setParentToDelete] = useState(null);
  const [editingParent, setEditingParent] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0
  });
  const [searchQuery, setSearchQuery] = useState('');

  // Get schoolId from user in localStorage
  // FIXED - handles both string and object formats safely
  const schoolId = (() => {
    try {
      const raw = localStorage.getItem("user");
      if (!raw) return null;

      const user = JSON.parse(raw);

      // Accept either string or object {_id}
      return user.school?._id || user.school || null;
    } catch (err) {
      console.error("Failed to read user from localStorage:", err);
      return null;
    }
  })();


  // Fetch parents with pagination and search
  const fetchParents = async (page = 1, limit = 50, q = '') => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (q) params.q = q;

      const response = await api.get('/parents', { params });
      setParents(response.data.parents || []);
      setPagination({
        page: response.data.meta?.page || page,
        limit: response.data.meta?.limit || limit,
        total: response.data.meta?.total || 0
      });
    } catch (err) {
      console.error('Error fetching parents:', err);
      toast.error('Failed to load parents');
    } finally {
      setLoading(false);
    }
  };

  // Fetch classes
  const fetchClasses = async () => {
    try {
      const response = await api.get('/classes');
      setClasses(Array.isArray(response.data) ? response.data : response.data.classes || []);
    } catch (err) {
      console.error('Error fetching classes:', err);
      toast.error('Failed to load classes');
    }
  };

  // Fetch students for selected class
  const fetchStudentsByClass = async (classId) => {
    try {
      if (!classId) {
        setStudents([]);
        return;
      }
      const response = await api.get(`/students?classId=${classId}`);
      const studentsData = Array.isArray(response.data) ? response.data : (response.data.students || []);
      setStudents(studentsData);
    } catch (err) {
      console.error('Error fetching students:', err);
      toast.error('Failed to load students');
    }
  };

  // Fetch all initial data
  const fetchInitialData = async () => {
    await Promise.all([
      fetchParents(),
      fetchClasses()
    ]);
  };

  useEffect(() => {
    if (schoolId) {
      fetchInitialData();
    } else {
      toast.error('School information not found');
    }
  }, [schoolId]);

  // Handle search with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchParents(1, pagination.limit, searchQuery);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const handleCheckbox = (id) => {
    setForm({
      ...form,
      childrenIds: form.childrenIds.includes(id)
        ? form.childrenIds.filter(c => c !== id)
        : [...form.childrenIds, id]
    });
  };

  const handleSubmit = async () => {
    if (!form.name || !form.email || (!form.password && !editingParent)) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate phone number format (Ghanaian format) if provided
    if (form.phone && !/^0\d{9}$/.test(form.phone)) {
      toast.error('Invalid phone number format. Use 0XXXXXXXXX (10 digits)');
      return;
    }

    setFormLoading(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        phone: form.phone || '',
        childrenIds: form.childrenIds
      };

      // Include password only for new parents or when updating with new password
      if (form.password) {
        payload.password = form.password;
      }

      if (editingParent) {
        // Update existing parent
        await api.put(`/parents/${editingParent._id}`, payload);
        toast.success('Parent updated successfully');
      } else {
        // Create new parent
        await api.post('/parents', payload);
        toast.success('Parent created successfully');
      }

      fetchParents(pagination.page, pagination.limit, searchQuery);
      setOpen(false);
      resetForm();
    } catch (err) {
      console.error('Error saving parent:', err.response?.data || err.message);
      const errorMessage = err.response?.data?.message || `Failed to ${editingParent ? 'update' : 'create'} parent`;
      toast.error(errorMessage);
    } finally {
      setFormLoading(false);
    }
  };

  const handleEdit = (parent) => {
    setEditingParent(parent);
    setForm({
      name: parent.name || '',
      email: parent.email || '',
      phone: parent.phone || '',
      password: '', // Don't pre-fill password for security
      childrenIds: parent.childIds ? parent.childIds.map(child =>
        typeof child === 'object' ? child._id : child
      ) : []
    });
    setOpen(true);
  };

  const handleDeleteClick = (parent) => {
    setParentToDelete(parent);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!parentToDelete) return;

    try {
      await api.delete(`/parents/${parentToDelete._id}`);
      toast.success('Parent deleted successfully');
      fetchParents(pagination.page, pagination.limit, searchQuery);
      setDeleteDialogOpen(false);
      setParentToDelete(null);
    } catch (err) {
      console.error('Error deleting parent:', err.response?.data || err.message);
      toast.error(err.response?.data?.message || 'Failed to delete parent');
    }
  };

  const getInitials = (name) => name ? name.split(' ').map(n => n[0]).join('').toUpperCase() : 'P';

  const resetForm = () => {
    setForm({ name: '', email: '', phone: '', password: '', childrenIds: [] });
    setSelectedClass('');
    setStudents([]);
    setEditingParent(null);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    resetForm();
  };

  // Helper function to get student name safely
  const getStudentName = (student) => {
    if (typeof student === 'object') {
      return student.user?.name || student.name || 'Unnamed Student';
    }
    return 'Unknown Student';
  };

  // Helper function to get admission number safely
  const getAdmissionNumber = (student) => {
    if (typeof student === 'object') {
      return student.admissionNumber || 'N/A';
    }
    return 'N/A';
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    fetchParents(newPage, pagination.limit, searchQuery);
  };

  // Mobile Parent Card Component
  const MobileParentCard = ({ parent }) => (
    <Card sx={{ mb: 2, boxShadow: 2 }}>
      <CardContent sx={{ p: 2 }}>
        <Stack spacing={2}>
          {/* Header with Parent Info */}
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Stack direction="row" alignItems="center" spacing={1}>
              <Avatar sx={{
                bgcolor: theme.palette.primary.main,
                width: 40,
                height: 40
              }}>
                {getInitials(parent.name)}
              </Avatar>
              <Box>
                <Typography variant="subtitle1" fontWeight="600">
                  {parent.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {parent.childIds?.length || 0} children
                </Typography>
              </Box>
            </Stack>
          </Stack>

          {/* Contact Information */}
          <Stack spacing={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <Email fontSize="small" color="action" />
              <Typography variant="body2" noWrap>
                {parent.email}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Phone fontSize="small" color="action" />
              <Typography variant="body2">
                {parent.phone || 'No phone'}
              </Typography>
            </Box>
          </Stack>

          {/* Linked Students */}
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
              LINKED STUDENTS
            </Typography>
            {parent.childIds && parent.childIds.length > 0 ? (
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {parent.childIds.slice(0, 2).map((child, index) => (
                  <Chip
                    key={index}
                    label={getStudentName(child)}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                ))}
                {parent.childIds.length > 2 && (
                  <Chip
                    label={`+${parent.childIds.length - 2} more`}
                    size="small"
                    color="default"
                    variant="outlined"
                  />
                )}
              </Stack>
            ) : (
              <Typography variant="caption" color="text.secondary">
                No students linked
              </Typography>
            )}
          </Box>

          {/* Action Buttons */}
          <ActionToolbar direction="row" spacing={1} justifyContent="space-between">
            <Tooltip title="Edit Parent">
              <Button
                variant="outlined"
                size="small"
                startIcon={<Edit />}
                onClick={() => handleEdit(parent)}
                sx={{ flex: 1 }}
              >
                Edit
              </Button>
            </Tooltip>
            <Tooltip title="Delete Parent">
              <Button
                variant="outlined"
                size="small"
                color="error"
                startIcon={<Delete />}
                onClick={() => handleDeleteClick(parent)}
                sx={{ flex: 1 }}
              >
                Delete
              </Button>
            </Tooltip>
          </ActionToolbar>
        </Stack>
      </CardContent>
    </Card>
  );

  return (
    <Container
      maxWidth="xl"
      sx={{
        py: isMobile ? 2 : 4,
        px: isMobile ? 1 : 2
      }}
    >
      {/* Header */}
      <PageHeader title={<>Parent Management</>} description={<>Manage parent accounts and link them to students</>} actions={<><Button variant="contained" startIcon={<Add />} onClick={() => setOpen(true)} size={isMobile ? "small" : "medium"} sx={{
          minWidth: isMobile ? 'auto' : 140,
          py: isMobile ? 0.75 : 1
        }}>
                      Add New Parent
                    </Button></>}></PageHeader>

      {/* Action Card */}
      <FilterBar
        sx={{
          mb: 4,
          boxShadow: isMobile ? 1 : 3,
          border: `1px solid ${theme.palette.divider}`
        }}
      >
        <CardContent
          sx={{
            p: isMobile ? 2 : isTablet ? 3 : 4
          }}
        >
          

          {/* Search and Actions */}
          <Stack
            direction={isMobile ? "column" : "row"}
            spacing={isMobile ? 1.5 : 2}
            alignItems={isMobile ? "stretch" : "center"}
            justifyContent="space-between"
          >
            <TextField
              placeholder="Search parents by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size={isMobile ? "small" : "medium"}
              sx={{
                minWidth: isMobile ? 'auto' : 300,
                flex: 1
              }}
              InputProps={{
                sx: { borderRadius: isMobile ? 1 : 2 }
              }}
            />


          </Stack>

          {/* Pagination Info */}
          {parents.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Showing {parents.length} of {pagination.total} parents
              </Typography>
            </Box>
          )}
        </CardContent>
      </FilterBar>

      {/* Parents Table Card */}
      <Card
        sx={{
          boxShadow: isMobile ? 1 : 3,
          border: `1px solid ${theme.palette.divider}`
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <Box
            sx={{
              p: isMobile ? 2 : 3,
              borderBottom: `1px solid ${theme.palette.divider}`
            }}
          >
            <Stack
              direction={isMobile ? "column" : "row"}
              justifyContent="space-between"
              alignItems={isMobile ? "flex-start" : "center"}
              spacing={isMobile ? 1 : 0}
            >
              <Typography
                variant={isMobile ? "h6" : "h6"}
                fontWeight="600"
                sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }}
              >
                Parent Accounts ({pagination.total})
              </Typography>
              {parents.length > 0 && (
                <Chip
                  label={`${parents.length} of ${pagination.total}`}
                  color="primary"
                  variant="outlined"
                  size={isMobile ? "small" : "medium"}
                />
              )}
            </Stack>
          </Box>

          {loading ? (
            <LoadingState />
          ) : parents.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
              <FamilyRestroom
                sx={{
                  fontSize: isMobile ? 48 : 64,
                  color: 'text.secondary',
                  opacity: 0.5,
                  mb: 2
                }}
              />
              <Typography
                variant={isMobile ? "h6" : "h6"}
                color="text.secondary"
                gutterBottom
                sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }}
              >
                {searchQuery ? 'No parents found matching your search' : 'No parents found'}
              </Typography>
              <Typography
                variant={isMobile ? "body2" : "body2"}
                color="text.secondary"
                sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}
              >
                {searchQuery ? 'Try adjusting your search terms' : 'Get started by adding your first parent account'}
              </Typography>
            </Box>
          ) : isMobile ? (
            // Mobile View - Cards
            <Box sx={{ p: 2 }}>
              <Stack spacing={1}>
                {parents.map((parent) => (
                  <MobileParentCard key={parent._id} parent={parent} />
                ))}
              </Stack>
            </Box>
          ) : (
            // Desktop/Tablet View - Table
            <Box sx={{
              width: '100%',
              overflow: 'auto',
              maxHeight: isTablet ? '60vh' : 'none'
            }}>
              <DataTable><Table sx={{ minWidth: isTablet ? 800 : 'auto' }}>
                <TableHead sx={{ bgcolor: theme.palette.background.default }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', py: 2 }}>PARENT</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', py: 2 }}>CONTACT INFO</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', py: 2 }}>CHILDREN</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', py: 2 }}>LINKED STUDENTS</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', py: 2 }}>ACTIONS</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
{parents.length === 0 && <TableRow><TableCell colSpan={100}><EmptyState title="No records found" description="Records will appear here when available. Check your selected filters." /></TableCell></TableRow>}

                  {parents.map((parent) => (
                    <TableRow
                      key={parent._id}
                      sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.02) } }}
                    >
                      <TableCell sx={{ py: 2 }}>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Avatar sx={{
                            bgcolor: theme.palette.primary.main,
                            width: 40,
                            height: 40
                          }}>
                            {getInitials(parent.name)}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle1" fontWeight="500">
                              {parent.name}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Stack spacing={0.5}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Email fontSize="small" color="action" />
                            <Typography variant="body2">
                              {parent.email}
                            </Typography>
                          </Box>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Phone fontSize="small" color="action" />
                            <Typography variant="body2">
                              {parent.phone || 'No phone number'}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography variant="subtitle1" fontWeight="500">
                          {parent.childIds?.length || 0}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        {parent.childIds && parent.childIds.length > 0 ? (
                          <Stack spacing={0.5}>
                            {parent.childIds.slice(0, 3).map((child, index) => (
                              <Chip
                                key={index}
                                label={getStudentName(child)}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            ))}
                            {parent.childIds.length > 3 && (
                              <Chip
                                label={`+${parent.childIds.length - 3} more`}
                                size="small"
                                color="default"
                                variant="outlined"
                              />
                            )}
                          </Stack>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No students linked
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Stack direction="row" spacing={1}>
                          <Tooltip title="Edit Parent">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleEdit(parent)}
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Parent">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteClick(parent)}
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table></DataTable>
            </Box>
          )}

          {/* Pagination Controls */}
          {parents.length > 0 && (
            <Box sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  Page {pagination.page} of {Math.ceil(pagination.total / pagination.limit)}
                </Typography>
                <ActionToolbar direction="row" spacing={1}>
                  <Button
                    size="small"
                    disabled={pagination.page <= 1}
                    onClick={() => handlePageChange(pagination.page - 1)}
                  >
                    Previous
                  </Button>
                  <Button
                    size="small"
                    disabled={pagination.page >= Math.ceil(pagination.total / pagination.limit)}
                    onClick={() => handlePageChange(pagination.page + 1)}
                  >
                    Next
                  </Button>
                </ActionToolbar>
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Parent Dialog */}
      <Dialog
        open={open}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 2,
            minHeight: isMobile ? '100vh' : '70vh'
          }
        }}
      >
        <DialogTitle sx={{
          bgcolor: theme.palette.primary.main,
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          py: isMobile ? 2 : 3
        }}>
          <Person />
          {editingParent ? 'Edit Parent' : 'Add New Parent'}
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: isMobile ? 2 : isTablet ? 3 : 4 }}>
            <Stack spacing={isMobile ? 3 : 4}>
              {/* Parent Information Section */}
              <Box>
                <Typography
                  variant={isMobile ? "subtitle1" : "h6"}
                  gutterBottom
                  fontWeight="600"
                  color="primary"
                  sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }}
                >
                  Parent Information
                </Typography>
                <Grid container spacing={isMobile ? 2 : 3}>
                  {[
                    { field: 'name', label: 'Full Name', type: 'text', required: true },
                    { field: 'email', label: 'Email Address', type: 'email', required: true },
                    { field: 'phone', label: 'Phone Number', type: 'tel', required: false },
                    { field: 'password', label: 'Password', type: 'password', required: !editingParent }
                  ].map(({ field, label, type, required }) => (
                    <Grid size={{ xs: 12 }}   key={field}>
                      <TextField
                        fullWidth
                        label={label}
                        name={field}
                        type={type}
                        onChange={(e) => setForm({
                          ...form,
                          [field]: field === 'name' ? e.target.value.toUpperCase() : e.target.value
                        })}
                        size={isMobile ? "small" : "medium"}
                        placeholder={`Enter ${label.toLowerCase()}`}
                        InputLabelProps={{
                          shrink: true,
                          sx: { fontWeight: 600, mb: 1 }
                        }}
                        helperText={
                          field === 'phone'
                            ? 'Optional - Format: 0XXXXXXXXX (10 digits)'
                            : required && field === 'password' && editingParent
                              ? 'Leave blank to keep current password'
                              : required
                                ? 'This field is required'
                                : ''
                        }
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: isMobile ? 1 : 2
                          }
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>

              <Divider />

              {/* Student Linking Section */}
              <Box>
                <Typography
                  variant={isMobile ? "subtitle1" : "h6"}
                  gutterBottom
                  fontWeight="600"
                  color="primary"
                  sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }}
                >
                  Link Students
                </Typography>

                {/* Class Selection */}
                <Box sx={{ mb: 3 }}>
                  <TextField
                    select
                    fullWidth
                    label="Select Class"
                    value={selectedClass}
                    onChange={(e) => {
                      setSelectedClass(e.target.value);
                      fetchStudentsByClass(e.target.value);
                    }}
                    size={isMobile ? "small" : "medium"}
                    InputLabelProps={{
                      shrink: true,
                      sx: { fontWeight: 600, mb: 1 }
                    }}
                    SelectProps={{
                      displayEmpty: true,
                      renderValue: (selected) => {
                        if (selected === '') {
                          return 'Select Class';
                        }
                        const selectedClassObj = classes.find(c => c._id === selected);
                        return selectedClassObj?.displayName || selectedClassObj?.name || selected;
                      }
                    }}
                    helperText="Choose a class to view available students"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: isMobile ? 1 : 2
                      }
                    }}
                  >
                    <MenuItem value="" disabled>
                      Select Class
                    </MenuItem>
                    {classes.map((cls) => (
                      <MenuItem key={cls._id} value={cls._id}>
                        {cls.displayName || cls.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>

                {/* Students List */}
                {students.length > 0 && (
                  <Box>
                    <Alert
                      severity="info"
                      sx={{
                        borderRadius: isMobile ? 1 : 2,
                        mb: 3
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight="600">
                        Available Students in Selected Class ({students.length})
                      </Typography>
                      <Typography variant="body2">
                        Select the students that belong to this parent
                      </Typography>
                    </Alert>

                    <Paper
                      variant="outlined"
                      sx={{
                        p: isMobile ? 1.5 : 3,
                        maxHeight: isMobile ? 400 : 450,
                        overflow: 'auto',
                        borderRadius: isMobile ? 1 : 2,
                        borderColor: 'divider'
                      }}
                    >
                      <Stack spacing={isMobile ? 1 : 2}>
                        {students.map((student) => (
                          <FormControlLabel
                            key={student._id}
                            control={
                              <Checkbox
                                checked={form.childrenIds.includes(student._id)}
                                onChange={() => handleCheckbox(student._id)}
                                icon={<ChildCare />}
                                checkedIcon={<ChildCare color="primary" />}
                                sx={{
                                  '& .MuiSvgIcon-root': {
                                    fontSize: isMobile ? 24 : 28
                                  }
                                }}
                              />
                            }
                            label={
                              <Box sx={{ ml: isMobile ? 1 : 2 }}>
                                <Typography
                                  variant={isMobile ? "body2" : "body1"}
                                  fontWeight="500"
                                >
                                  {getStudentName(student)}
                                </Typography>
                                <Typography
                                  variant={isMobile ? "caption" : "body2"}
                                  color="text.secondary"
                                >
                                  Admission: {getAdmissionNumber(student)}
                                </Typography>
                              </Box>
                            }
                            sx={{
                              width: '100%',
                              m: 0,
                              p: isMobile ? 1 : 2,
                              borderRadius: isMobile ? 1 : 2,
                              border: '1px solid',
                              borderColor: form.childrenIds.includes(student._id)
                                ? 'primary.main'
                                : 'divider',
                              backgroundColor: form.childrenIds.includes(student._id)
                                ? alpha(theme.palette.primary.main, 0.04)
                                : 'transparent',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                borderColor: 'primary.main',
                                backgroundColor: alpha(theme.palette.primary.main, 0.02)
                              }
                            }}
                          />
                        ))}
                      </Stack>
                    </Paper>

                    {/* Selection Summary */}
                    {form.childrenIds.length > 0 && (
                      <Alert
                        severity="success"
                        sx={{
                          mt: 3,
                          borderRadius: isMobile ? 1 : 2
                        }}
                      >
                        <Box>
                          <Typography variant="subtitle2" gutterBottom fontWeight="600">
                            {form.childrenIds.length} Student(s) Selected
                          </Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {students
                              .filter(student => form.childrenIds.includes(student._id))
                              .map((student) => (
                                <Chip
                                  key={student._id}
                                  label={getStudentName(student)}
                                  size="small"
                                  color="primary"
                                  variant="filled"
                                  onDelete={() => handleCheckbox(student._id)}
                                />
                              ))
                            }
                          </Stack>
                        </Box>
                      </Alert>
                    )}
                  </Box>
                )}

                {selectedClass && students.length === 0 && (
                  <Alert severity="warning" sx={{ borderRadius: isMobile ? 1 : 2 }}>
                    <Typography variant="body2">
                      No students found in the selected class
                    </Typography>
                  </Alert>
                )}
              </Box>
            </Stack>
          </Box>
        </DialogContent>

        <DialogActions sx={{
          p: isMobile ? 2 : 3,
          gap: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
          flexDirection: isMobile ? 'column' : 'row'
        }}>
          <Button
            onClick={handleCloseDialog}
            variant="outlined"
            fullWidth={isMobile}
            size={isMobile ? "medium" : "large"}
            sx={{
              minWidth: isMobile ? 'auto' : 120,
              borderRadius: isMobile ? 1 : 2
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={formLoading}
            fullWidth={isMobile}
            size={isMobile ? "medium" : "large"}
            startIcon={formLoading ? <CircularProgress size={20} /> : null}
            sx={{
              minWidth: isMobile ? 'auto' : 180,
              borderRadius: isMobile ? 1 : 2
            }}
          >
            {formLoading
              ? (editingParent ? 'Updating...' : 'Creating...')
              : (editingParent ? 'Update Parent' : 'Create Parent')
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 2
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${theme.palette.divider}`,
          py: isMobile ? 1.5 : 2
        }}>
          <Typography
            variant={isMobile ? "h6" : "h6"}
            fontWeight="600"
            sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }}
          >
            Confirm Delete
          </Typography>
          <IconButton
            size="small"
            onClick={() => setDeleteDialogOpen(false)}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ py: 3 }}>
          <Stack spacing={2} alignItems="center">
            <Avatar sx={{
              bgcolor: theme.palette.error.main,
              width: isMobile ? 50 : 60,
              height: isMobile ? 50 : 60
            }}>
              <Person fontSize={isMobile ? "medium" : "large"} />
            </Avatar>
            <Typography
              variant={isMobile ? "h6" : "h6"}
              textAlign="center"
              sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }}
            >
              Delete {parentToDelete?.name}?
            </Typography>
            <Typography
              variant={isMobile ? "body2" : "body2"}
              color="text.secondary"
              textAlign="center"
              sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}
            >
              This action cannot be undone. All linked student associations will be removed.
            </Typography>
            {parentToDelete?.childIds?.length > 0 && (
              <Alert severity="warning" sx={{ width: '100%' }}>
                <Typography variant="body2">
                  This parent is linked to {parentToDelete.childIds.length} student(s).
                  These associations will be removed.
                </Typography>
              </Alert>
            )}
          </Stack>
        </DialogContent>

        <DialogActions sx={{
          p: isMobile ? 2 : 3,
          gap: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
          flexDirection: isMobile ? 'column' : 'row'
        }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            variant="outlined"
            fullWidth={isMobile}
            size={isMobile ? "medium" : "large"}
            sx={{
              minWidth: isMobile ? 'auto' : 120,
              borderRadius: isMobile ? 1 : 2
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            fullWidth={isMobile}
            size={isMobile ? "medium" : "large"}
            startIcon={<Delete />}
            sx={{
              minWidth: isMobile ? 'auto' : 120,
              borderRadius: isMobile ? 1 : 2
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}